import React, { Suspense, useEffect, useState } from "react";
import * as routes from "../../../config/routePaths";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, Link, Outlet } from "react-router-dom";
import Loading from "../../Template/Loading/Loading";
import { BACKGROUNDS } from "../../../utils/backgroundUtils";
import {
  Box,
  Paper,
  Typography,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Divider,
  Tooltip,
  useTheme,
  Badge,
  alpha,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import {
  Phone,
  MicOff,
  VolumeOff,
  PhoneDisabled,
  SettingsPhone,
  HeadsetMic,
  History,
  Contacts,
  Message,
  Voicemail,
  LocalParking as Park,
  Settings,
  Analytics,
  Logout,
  Feedback,
  DarkMode,
  DarkModeOutlined,
  Notifications,
  NotificationsActive
} from "@mui/icons-material";
import logo from "../../../assets/images/logo-light.png";
import { selectNotificationState, selectNotificationsEnabled } from "../../../state/notification/notificationSlice";
import { selectMessageSessions, setSelectedMessageSessionId } from "../../../state/message/messageSessionSlice";
import { selectUserCredentials } from "../../../state/userCredential/userCredentialSlice";
import { selectLogin } from "../../../state/login/loginSlice";
import { logoutRequestAction } from "../../../sagas/logout/action";
import { SnackbarProvider } from "../../Template/SnackbarContext/SnackbarContext";
import { generateBadge } from "../../../utils/badgeGenerator";
import { handleCallNotificationAction } from "../../../sagas/notification/handleCallNotification/action";
import { loadAudioDeviceAction } from "../../../sagas/audioDevice/loadAudioDevice/action";
import { selectDefaultBackground } from "../../../state/background/backgroundSlice";
import IncomingCallsContainer from "../../atoms/Phone/IncomingCallsContainer";
import ActiveCallScreen from "../../atoms/Phone/ActiveCallScreen";
import Assistant from "../Assistant/Assistant";
import NotificationPanel from "../../atoms/Sidebar/NotificationPanel";
import { showSnackbar } from "../../../state/snackbar/snackbarSlice";
import { selectSelfPresence } from "../../../state/presence/presenceSlice";
import { selectAudioDevices } from "../../../state/audioDevice/audioDeviceSlice";
import { selectTotalParkedCalls } from "../../../state/callPark/callParkSlice";
import { selectNewVoicemail } from "../../../state/voicemail/voicemailSlice";
import {
  toggleDoNotDisturb,
  selectDoNotDisturb,
  selectUA,
  selectUAState,
} from "../../../state/userAgent/userAgentSlice";
import { fetchMessageSessionAction } from "../../../sagas/message/fetchMessageSession/action";
import { systemSuspendAction } from "../../../sagas/systemSuspend/action";
import { systemResumeAction } from "../../../sagas/systemResume/action";
import { initiateCallAction } from "../../../sagas/userAgent/initiateCall/action";
import { addTemporaryMessageSessionAction } from "../../../sagas/message/handleTemporaryMessageSession/action";
import { selectSMSNumbers } from "../../../state/device/deviceSlice";
import { generateNewSessionId } from "../../../utils/messageUtils";
import { isValidPhoneNumber, processNumber } from "../../../utils/phoneUtils";
import { getDefaultTelAction } from "../../../browser/storage";
import { onTelProtocol, removeTelProtocol } from "../../../browser/telLaunch";
import moment from "moment";
import {
  setUnreadMessageBadge,
  onCallNotificationAction,
  removeCallNotificationAction,
  onMessageNotificationClick,
  onRestoreNotificationBadge,
} from "../../../browser/notifications";
import { onSuspend, onResume } from "../../../browser/lifecycle";

const drawerWidth = 220;
const miniDrawerWidth = 75;
const appBarHeight = 64;
const titleBarHeight = 0;
const assistantOffsetHeight = 200;


const Sidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);

  const ua = useSelector(selectUA);
  const uaState = useSelector(selectUAState);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const defaultBackground = useSelector(selectDefaultBackground);
  const selectedBg = BACKGROUNDS[defaultBackground];

  const isLoggedIn = useSelector(selectLogin).isLoggedIn;
  const { unreadCount: unreadNotificationCount } = useSelector(selectNotificationState);
  const notificationsEnabled = useSelector(selectNotificationsEnabled);

  const { uid: currentUserUid, domain, displayName, user } = useSelector(selectUserCredentials);
  const smsNumbers = useSelector(selectSMSNumbers);
  const { selectedMessageSessionId, messageSession } = useSelector(
    selectMessageSessions
  );
  const totalParkedCalls = useSelector(selectTotalParkedCalls);
  const newVoicemails = useSelector(selectNewVoicemail);
  const presence = useSelector(selectSelfPresence);
  const doNotDisturb = useSelector(selectDoNotDisturb);
  const { speakers, microphones } = useSelector(selectAudioDevices);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);


  const getRegistrationStatus = () => {
    if (!ua) return { color: theme.palette.error.main, label: "Unregistered", icon: <PhoneDisabled sx={{ color: "white", fontSize: "1.1rem" }} /> };
    if (uaState !== 'registered') return { color: theme.palette.customColors.phoneStatusDisabled, label: "Connecting...", icon: <SettingsPhone sx={{ color: "white", fontSize: "1.1rem" }} /> };
    if (microphones.length === 0) return { color: theme.palette.customColors.phoneStatusDisabled, label: "No Microphone Detected", icon: <MicOff sx={{ color: "white", fontSize: "1.1rem" }} /> };
    if (speakers.length === 0) return { color: theme.palette.customColors.phoneStatusDisabled, label: "No Speakers Detected", icon: <VolumeOff sx={{ color: "white", fontSize: "1.1rem" }} /> };
    return { color: theme.palette.customColors.phoneStatusActive, label: "Registered", icon: <Phone sx={{ color: "white", fontSize: "1.1rem" }} /> };
  };


  const getAvailabilityStatus = () => {
    if (doNotDisturb) return { color: theme.palette.contactColors.mutedRed, label: "Do Not Disturb", shortLabel: "DND" };
    return presence === "open"
      ? { color: theme.palette.customColors.phoneStatusActive, label: "Available", shortLabel: "Active" }
      : { color: theme.palette.customColors.phoneStatusInactive, label: "Busy", shortLabel: "Busy" };
  };

  const getBackgroundStyle = () => {
    if (!selectedBg) return {};

    switch (selectedBg.type) {
      case 'image':
        return {
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${selectedBg.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        };
      case 'color':
        return {
          backgroundColor: selectedBg.color,
        };
      case 'gradient':
        return {
          backgroundImage: selectedBg.gradient,
        };
      default:
        return {};
    }
  };


  const handleDoNotDisturbToggle = () => {
    dispatch(toggleDoNotDisturb());
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate(routes.getLoginRoute());
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (
      selectedMessageSessionId !== null &&
      location.pathname !== routes.getMessageRoute()
    ) {
      navigate(routes.getMessageRoute(), { state: { from: "sidebar" } });
    }
  }, [selectedMessageSessionId]);

  useEffect(() => {
    const count = messageSession.filter(
      (session) => session["messagesession-last-status"] === "unread"
    ).length;
    setUnreadMessageCount(count);
    // Set badge on an app icon
    if (count > 0) {
      const badgeDataUrl = generateBadge(count);
      setUnreadMessageBadge(count, badgeDataUrl);
    } else {
      setUnreadMessageBadge(0, null);
    }
  }, [messageSession]);

  useEffect(() => {
    // Audio Device change listener
    const handleDeviceChange = () => {
      dispatch(loadAudioDeviceAction());
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange
      );
    };
  }, [dispatch]);

  useEffect(() => {
    const handleNotificationAction = ({ notificationAction, callId }) => {
      dispatch(handleCallNotificationAction({ notificationAction, callId }));
    };
    onCallNotificationAction(handleNotificationAction);
    return () => {
      removeCallNotificationAction();
    };
  }, [dispatch]);

  useEffect(() => {
    return onMessageNotificationClick((sessionId) => {
      dispatch(setSelectedMessageSessionId(sessionId));
    });
  }, [dispatch]);

  // OS tel: links. Register only once SIP is registered so a number dialed
  // during startup stays buffered in telLaunch until the app can act on it.
  useEffect(() => {
    if (uaState !== "registered") return undefined;

    const openMessageThread = (number) => {
      const existingSession = messageSession.find(
        (session) =>
          processNumber(session["messagesession-remote"]) ===
          processNumber(number)
      );
      if (existingSession) {
        dispatch(setSelectedMessageSessionId(existingSession["messagesession-id"]));
        return;
      }
      const type = isValidPhoneNumber(number) ? "sms" : "chat";
      const smsNumber = type === "sms" && smsNumbers.length ? smsNumbers[0] : null;
      const newSessionId = generateNewSessionId(
        number,
        currentUserUid,
        domain,
        smsNumber
      );
      dispatch(
        addTemporaryMessageSessionAction({
          "messagesession-id": newSessionId,
          "messagesession-remote": number,
          "messagesession-last-type": type,
          "messagesession-last-status": "temporary",
          "messagesession-last-message": "",
          "messagesession-last-datetime": moment()
            .utcOffset(0)
            .format("YYYY-MM-DD HH:mm:ss"),
          "messagesession-sms-number": type === "sms" && smsNumber ? smsNumber : "",
        })
      );
      dispatch(setSelectedMessageSessionId(newSessionId));
    };

    const handleTelProtocol = (phoneNumber) => {
      getDefaultTelAction().then((val) => {
        if (val === "message") openMessageThread(phoneNumber);
        else dispatch(initiateCallAction({ phoneNumber }));
      });
    };

    onTelProtocol(handleTelProtocol);
    return () => removeTelProtocol();
  }, [uaState, dispatch, messageSession, smsNumbers, currentUserUid, domain]);

  useEffect(() => {
    const removeRestoreNotificationBadgeListener =
      onRestoreNotificationBadge(() => {
        dispatch(fetchMessageSessionAction());
      });

    const removeSuspendListener = onSuspend(() => {
      dispatch(systemSuspendAction());
    });

    const removeResumeListener = onResume(() => {
      dispatch(systemResumeAction());
    });

    return () => {
      removeRestoreNotificationBadgeListener();
      removeSuspendListener();
      removeResumeListener();
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      console.debug("Network came back online. Attempting to reconnect.");
      dispatch(
        showSnackbar({
          message: "Network came back online. Attempting to reconnect.",
          severity: "success",
        })
      );
    };
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const handleLogout = () => {
    dispatch(logoutRequestAction());
  };

  const StatusIndicator = () => {
    const registrationStatus = getRegistrationStatus();
    const availStatus = getAvailabilityStatus();

    return (
      <Paper elevation={1} sx={{
        display: "flex", alignItems: "center",
        height: appBarHeight - 16, borderRadius: 1.5,
        backgroundColor: alpha(theme.palette.background.paper, 0.1),
        overflow: "hidden",
      }}>
        <Tooltip title={registrationStatus.label} arrow>
          <Box sx={{
            display: "flex", alignItems: "center", px: 1.5,
            height: "100%", cursor: "pointer",
            transition: "background 0.2s",
            "&:hover": { backgroundColor: alpha(theme.palette.background.paper, 0.15) },
          }}>
            <Avatar sx={{ bgcolor: registrationStatus.color, width: 32, height: 32 }}>
              {registrationStatus.icon}
            </Avatar>
          </Box>
        </Tooltip>

        <Box sx={{ width: "1px", height: 20, bgcolor: alpha(theme.palette.divider, 0.4), flexShrink: 0 }} />

        <Tooltip title={availStatus.label} arrow>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.75, px: 1.5,
            height: "100%", cursor: "pointer",
            transition: "background 0.2s",
            "&:hover": { backgroundColor: alpha(theme.palette.background.paper, 0.15) },
          }}>
            <Box sx={{
              width: 10, height: 10, borderRadius: "50%",
              bgcolor: availStatus.color, flexShrink: 0,
            }} />
            <Typography variant="body2" sx={{ color: availStatus.color, fontWeight: "medium" }}>
              {availStatus.shortLabel}
            </Typography>
          </Box>
        </Tooltip>
      </Paper>
    );
  };

  const menuItems = [
    {
      text: "Phone",
      icon: <Phone fontSize="large" />,
      path: routes.getPhoneRoute(),
    },
    {
      text: "Call History",
      icon: <History fontSize="large" />,
      path: routes.getCdrRoute(),
    },
    {
      text: "Contacts",
      icon: <Contacts fontSize="large" />,
      path: routes.getContactRoute(),
    },
    {
      text: "Messages",
      icon: (
        <Badge badgeContent={unreadMessageCount} color="secondary">
          <Message fontSize="large" />
        </Badge>
      ),
      path: routes.getMessageRoute(),
    },
    {
      text: "Voicemail",
      icon: (
        <Badge badgeContent={newVoicemails.length} color="secondary">
          <Voicemail fontSize="large" />
        </Badge>
      ),
      path: routes.getVoicemailRoute(),
    },
    {
      text: "Call Park",
      icon: (
        <Badge badgeContent={totalParkedCalls} color="secondary">
          <Park fontSize="large" />
        </Badge>
      ),
      path: routes.getCallParkRoute(),
    },
    {
      text: "Call Center",
      icon: <HeadsetMic fontSize="large" />,
      path: routes.getCallCenterRoute(),
    },
    {
      text: 'AI Analytics',
      icon: <Analytics fontSize="large" />,
      path: routes.getAnalyticsRoute(),
    },
    {
      text: "Settings",
      icon: <Settings fontSize="large" />,
      path: routes.getSettingRoute(),
    },
    // // Feedback page hidden for now
    // {
    //   text: "Feedback",
    //   icon: <Feedback fontSize="large" />,
    //   path: routes.getFeedbackRoute(),
    // },
  ];

  return (
    <SnackbarProvider>
      <Box sx={{
        display: "flex",
        height: "100vh",
        ...getBackgroundStyle(),
      }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: alpha(theme.palette.background.paper, 0.4),
            backdropFilter: "blur(8px)",
            height: `${appBarHeight}px`,
            top: `${titleBarHeight}px`,
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <img
              src={logo}
              alt="Comet3 Desktop Logo"
              style={{
                height: "45px",
              }}
            />
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <StatusIndicator />

              {/* DND */}
              <Tooltip
                title={doNotDisturb ? "Disable DND" : "Enable DND"}
                arrow
              >
                <IconButton
                  onClick={handleDoNotDisturbToggle}
                  sx={{
                    color: doNotDisturb
                      ? theme.palette.contactColors.mutedRed
                      : theme.palette.secondary.text,
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {doNotDisturb ? <DarkMode /> : <DarkModeOutlined />}
                </IconButton>
              </Tooltip>


              {/* Notification */}

              <Tooltip
                title={
                  notificationsEnabled
                    ? unreadNotificationCount > 0
                      ? `${unreadNotificationCount} new notifications`
                      : "No new notifications"
                    : "Notifications disabled"
                }
                arrow
              >
                <IconButton
                  onClick={(e) => {
                    setNotifPanelOpen(true);
                    setNotifAnchorEl(e.currentTarget);
                  }}
                  sx={{
                    color: notificationsEnabled
                      ? theme.palette.secondary.text
                      : theme.palette.action.disabled,
                    opacity: notificationsEnabled ? 1 : 0.5,
                    transition: "0.2s",
                  }}
                >
                  <Badge badgeContent={unreadNotificationCount} color="error">
                    {unreadNotificationCount > 0 ? (
                      <NotificationsActive fontSize="medium" />
                    ) : (
                      <Notifications fontSize="medium" />
                    )}
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          sx={{
            width: isMobile ? miniDrawerWidth : drawerWidth,
            flexShrink: 0,
            whiteSpace: "nowrap",
            "& .MuiDrawer-paper": {
              width: isMobile ? miniDrawerWidth : drawerWidth,
              boxSizing: "border-box",
              backgroundColor: alpha(theme.palette.background.paper, 0.4),
              backdropFilter: "blur(8px)",
              transition: "width 0.2s ease-in-out",
              overflowX: "hidden",
              marginTop: `${titleBarHeight}px`,
            },

          }}
        >
          <Toolbar />
          <Box
            sx={{
              overflowY: "auto",
              height: `calc(100vh - ${appBarHeight + titleBarHeight}px)`,
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.text.secondary, 0.05),
                borderRadius: "4px",
                marginTop: `${appBarHeight - titleBarHeight}px`,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.secondary, 0.5),
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.text.secondary, 0.8),
                },
              },
            }}
          >
            <List>
              <ListItem disablePadding>
                <Tooltip title={`${displayName} · ${user}`} placement="right" arrow>
                  <Box sx={{ display: "flex", alignItems: "center", px: 2.5, minWidth: 0, width: "100%" }}>
                    <Avatar sx={{
                      width: 28, height: 28, fontSize: "0.75rem", fontWeight: 600,
                      bgcolor: alpha(theme.palette.common.white, 0.9),
                      color: theme.palette.getContrastText(theme.palette.common.white),
                      mr: isMobile ? "auto" : 3, ml: "5px",
                    }}>
                      {(displayName || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                    </Avatar>
                    {!isMobile && (
                      <ListItemText
                        primary={displayName}
                        secondary={user}
                        primaryTypographyProps={{ noWrap: true, fontWeight: "medium", fontSize: "0.85rem", lineHeight: 1.2 }}
                        secondaryTypographyProps={{ noWrap: true, fontSize: "0.72rem", lineHeight: 1.2 }}
                        sx={{ my: 0 }}
                      />
                    )}
                  </Box>
                </Tooltip>
              </ListItem>
              {menuItems.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ mt: 2.25 }}>
                  <Tooltip
                    title={isMobile ? item.text : ""}
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      selected={location.pathname === item.path}
                      onAuxClick={(e) => {
                        e.preventDefault();
                      }}
                      sx={{
                        minHeight: 48,
                        justifyContent: isMobile ? "center" : "initial",
                        px: 2.5,
                        position: "relative",
                        "&.Mui-selected": {
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "4px",
                            height: "95%",
                            backgroundColor: "secondary.main",
                            borderRadius: "4px",
                          },
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                          "& .MuiListItemIcon-root": {
                            color: "primary.contrastText",
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isMobile ? "auto" : 3,
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!isMobile && <ListItemText primary={item.text} />}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
              <Divider sx={{ my: 1.2 }} />
              <ListItem disablePadding>
                <Tooltip
                  title={isMobile ? "Logout" : ""}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={handleLogout}
                    onAuxClick={(e) => {
                      e.preventDefault();
                    }}
                    sx={{
                      minHeight: 48,
                      justifyContent: isMobile ? "center" : "initial",
                      px: 2.5,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isMobile ? "auto" : 3,
                        justifyContent: "center",
                      }}
                    >
                      <Logout fontSize="large" />
                    </ListItemIcon>
                    {!isMobile && <ListItemText primary="Logout" />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            </List>
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // overflow: 'hidden auto',
            overflow: "hidden",
            mt: `${appBarHeight}px`,
            transition: "margin-left 0.2s ease-in-out",
            backgroundColor: alpha(theme.palette.background.default, 0),
          }}
        >
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </Box>
        <ActiveCallScreen
          drawerWidth={isMobile ? miniDrawerWidth : drawerWidth}
          appBarHeight={appBarHeight}
        />
        <IncomingCallsContainer appBarHeight={appBarHeight} />
        {/* <Assistant topOffset={appBarHeight + titleBarHeight + assistantOffsetHeight} /> */}
        <NotificationPanel
          open={notifPanelOpen}
          anchorEl={notifAnchorEl}
          onClose={() => {
            setNotifPanelOpen(false);
            setNotifAnchorEl(null);
          }}
        />

      </Box>
    </SnackbarProvider>
  );
};

export default Sidebar;
